/**
 * Auth store — Supabase magic-link auth + guest mode (P4-15 / P4-18).
 *
 * Guest mode: users get GUEST_LIMIT free questions before being prompted to log in.
 * Counts are persisted in localStorage so they survive page reloads.
 *
 * Auth flow:
 *   1. User enters email → POST /auth/magic-link
 *   2. Supabase emails a link; user clicks it → redirected back with #access_token=…
 *   3. initAuth() parses the fragment on mount, stores token in localStorage
 *   4. Token is sent as Authorization: Bearer <token> header on every /ask call
 */

import { create } from "zustand";

const GUEST_COUNT_KEY = "scoutdex_guest_count";
const AUTH_TOKEN_KEY = "scoutdex_auth_token";
const GUEST_LIMIT = 50;

interface AuthState {
  /** Supabase JWT — null when unauthenticated */
  token: string | null;
  /** Number of questions asked as guest */
  guestCount: number;
  /** True when a valid token is present */
  isAuthenticated: boolean;
  /** True when the user can still ask (authenticated OR guest slots remain) */
  canAsk: boolean;

  /** Call once on app mount — restores token + guest count, parses URL fragment */
  initAuth: () => void;
  /** Store a JWT received from Supabase magic-link redirect */
  setToken: (token: string) => void;
  /** Increment guest question counter (call after each successful /ask) */
  incrementGuest: () => void;
  /** Clear auth state (sign out) */
  signOut: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  guestCount: 0,
  isAuthenticated: false,
  canAsk: true,

  initAuth: () => {
    // 1. Check for magic-link token in URL fragment (#access_token=…&type=magiclink)
    const hash = window.location.hash;
    if (hash.includes("access_token=")) {
      const params = new URLSearchParams(hash.slice(1));
      const token = params.get("access_token");
      if (token) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        // Clean the URL fragment so it doesn't show in the address bar
        window.history.replaceState(null, "", window.location.pathname);
        set({ token, isAuthenticated: true, canAsk: true });
        return;
      }
    }

    // 2. Restore from localStorage
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedCount = parseInt(localStorage.getItem(GUEST_COUNT_KEY) ?? "0", 10);
    const guestCount = isNaN(storedCount) ? 0 : storedCount;

    if (storedToken) {
      set({ token: storedToken, isAuthenticated: true, guestCount, canAsk: true });
    } else {
      set({ token: null, isAuthenticated: false, guestCount, canAsk: guestCount < GUEST_LIMIT });
    }
  },

  setToken: (token: string) => {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    set({ token, isAuthenticated: true, canAsk: true });
  },

  incrementGuest: () => {
    const { isAuthenticated, guestCount } = get();
    if (isAuthenticated) return; // don't count authenticated users
    const next = guestCount + 1;
    localStorage.setItem(GUEST_COUNT_KEY, String(next));
    set({ guestCount: next, canAsk: next < GUEST_LIMIT });
  },

  signOut: () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(GUEST_COUNT_KEY);
    set({ token: null, isAuthenticated: false, guestCount: 0, canAsk: true });
  },
}));
